import React from "react";

import { Dimension } from "@superblocksteam/shared";

import type { ReduxAction } from "legacy/constants/ReduxActionConstants";
import type { WidgetType } from "legacy/constants/WidgetConstants";
import type { WidgetPropertyValidationType } from "legacy/constants/WidgetValidation";
import type { DataTreeWidget } from "legacy/entities/DataTree/dataTreeFactory";
import type {
  CanvasWidgetsReduxState,
  FlattenedWidgetProps,
} from "legacy/reducers/entityReducers/canvasWidgetsReducer";
import type { WidgetMetadata } from "legacy/reducers/entityReducers/metaReducer";
import type { APP_MODE } from "legacy/reducers/types";
import type { GeneratedTheme } from "legacy/themes";
import type { DynamicVisibilityProperties } from "legacy/widgets/BaseWidget";
import type {
  WidgetLayoutProps,
  FlattenedWidgetLayoutMap,
} from "legacy/widgets/shared";
import type { Flags } from "store/slices/featureFlags";

import type { default as BaseWidget, WidgetProps } from "./BaseWidget";

type DynamicWidgetsVisibilityState = Record<
  string,
  DynamicVisibilityProperties
>;

export { type CanvasWidgetsReduxState };
export type DerivedPropertiesMap = Record<string, string>;
export type WidgetActionResponse<WidgetType = FlattenedWidgetProps> = {
  widgetUpdates: Array<{
    widgetId: string;
    widget: WidgetType;
  }>;
  widgetMetaUpdates: Array<{
    widgetId: string;
    updates?: WidgetMetadata;
    reset?: boolean;
    resetOptions?: {
      propertyNames?: string[];
    };
  }>;
};

type WidgetActionHookParams = {
  widgetId: string;
  widgets: Readonly<CanvasWidgetsReduxState>;
  action: ReduxAction<any>;
  originalWidgetValues: Record<string, any>;
  flags: Flags;
  appMode: APP_MODE;
  theme: GeneratedTheme;
  dynamicWidgetLayout?: any;
  widgetsRuntime: CanvasWidgetsReduxState;
  widgetMetaProps: WidgetMetadata;
  evaluatedWidgets: Record<string, DataTreeWidget>;
  previousAiWidgets: Record<string, DataTreeWidget>;
};

export type WidgetActionHookSync = (
  params: WidgetActionHookParams,
) => WidgetActionResponse;

export type WidgetStaticFunctions = {
  [key in Exclude<
    keyof typeof BaseWidget,
    keyof typeof React.Component
  >]: (typeof BaseWidget)[key];
} & {
  applyActionHook?: WidgetActionHookSync;
  computeMinHeightFromProps?: (
    props: any,
    widgets: CanvasWidgetsReduxState | FlattenedWidgetLayoutMap,
    theme: GeneratedTheme,
    appMode: APP_MODE,
    dynamicVisibility: DynamicWidgetsVisibilityState,
  ) => Dimension<"px" | "gridUnit"> | undefined;
} & WidgetOperationsState;

interface WidgetOperationsState {
  enhancements?: {
    child: {
      autocomplete?: (
        parentProps: WidgetProps & {
          childAutocomplete: Record<string, unknown>;
        },
      ) => Record<string, unknown>;
    };
  };
}

interface WidgetConfig {
  validations: WidgetPropertyValidationType;
  derivedProperties: DerivedPropertiesMap;
  defaultProperties: Record<string, string>;
}

export type WidgetTypeConfigMap = Record<string, WidgetConfig>;

class WidgetFactory {
  static widgetMap: Map<
    WidgetType,
    React.ComponentType<React.PropsWithChildren<WidgetLayoutProps>>
  > = new Map();
  static widgetPropValidationMap: Map<
    WidgetType,
    WidgetPropertyValidationType
  > = new Map();
  static derivedPropertiesMap: Map<WidgetType, DerivedPropertiesMap> =
    new Map();
  static defaultPropertiesMap: Map<WidgetType, Record<string, string>> =
    new Map();
  static metaPropertiesMap: Map<WidgetType, Record<string, any>> = new Map();
  static widgetClasses: Map<WidgetType, WidgetStaticFunctions> = new Map();

  static getWidgetTypes(): WidgetType[] {
    return Array.from(this.widgetMap.keys());
  }

  static getWidgetDerivedPropertiesMap(
    widgetType: WidgetType,
  ): DerivedPropertiesMap {
    const map = this.derivedPropertiesMap.get(widgetType);
    if (!map) {
      console.error("Widget type validation is not defined");
      return {};
    }
    return map;
  }

  static getWidgetDefaultPropertiesMap(
    widgetType: WidgetType,
  ): Record<string, string> {
    const map = this.defaultPropertiesMap.get(widgetType);
    if (!map) {
      console.error("Widget default properties not defined", widgetType);
      return {};
    }
    return map;
  }

  static getWidgetMetaPropertiesMap(
    widgetType: WidgetType,
  ): Record<string, any> {
    const map = this.metaPropertiesMap.get(widgetType);
    if (!map) {
      console.error("Widget meta properties not defined: ", widgetType);
      return {};
    }
    return map;
  }

  static getWidgetSetup(): Partial<
    Record<
      WidgetType,
      {
        enhancements: WidgetStaticFunctions["enhancements"];
      }
    >
  > {
    const typeConfigMap: Partial<
      Record<
        WidgetType,
        {
          enhancements: WidgetStaticFunctions["enhancements"];
        }
      >
    > = {};
    WidgetFactory.getWidgetTypes().forEach((type) => {
      const widget = WidgetFactory.widgetClasses.get(type);
      typeConfigMap[type] = {
        enhancements: widget?.enhancements,
      };
    });
    return typeConfigMap;
  }

  static getWidgetClasses(): Record<string, WidgetStaticFunctions> {
    return Object.fromEntries(WidgetFactory.widgetClasses.entries());
  }

  static getWidgetTypeConfigMap(): WidgetTypeConfigMap {
    return {};
  }

  static getWidgetParentChildSetup(): Partial<
    Record<WidgetType, { allowedChildTypes: string[] }>
  > {
    return {};
  }

  static getWidgetComputedHeight(..._args: any[]): Dimension<"px"> | undefined {
    return undefined;
  }

  static getWidgetMinimumHeight(..._args: any[]): Dimension<"px"> | undefined {
    return undefined;
  }

  static getWidgetDocUrl(_type?: WidgetType): string | undefined {
    return undefined;
  }

  static createWidget(_widgetData: any, _appMode: any): React.ReactNode {
    return null;
  }
}

export default WidgetFactory;
